/** Shared option arrays used by BienDetailPage & other detail pages.
 *  Each getter takes a `t` function from react-i18next to produce translated labels. */

import type { TFunction } from "i18next";
import { today } from "@/lib/today";

// Types de bien — famille appartement (client Skyline = 100% appartements).
// La VALEUR est une clé neutre alignée sur l'enum Immoweb
// `estateSubType.apartment` ; estateType/estateSubType en sont dérivés
// (cf. src/lib/immoweb/estateType.ts → deriveEstate). Étendre ici pour les
// maisons (HOUSE + sous-types) si un client non-appartement arrive.
export const getTypesBien = (t: TFunction) => [
  { value: "APARTMENT", label: t("options.typesBien.appartement") },
  { value: "PENTHOUSE", label: t("options.typesBien.penthouse") },
  { value: "STUDIO", label: t("options.typesBien.studio") },
  { value: "DUPLEX", label: t("options.typesBien.duplex") },
  { value: "TRIPLEX", label: t("options.typesBien.triplex") },
  { value: "LOFT", label: t("options.typesBien.loft") },
  { value: "GROUND_FLOOR", label: t("options.typesBien.rdc") },
  { value: "KOT", label: t("options.typesBien.kot") },
];

export const getTypesEnvironnement = (t: TFunction) => [
  { value: "", label: "—" },
  { value: "Urbain", label: t("options.environnement.urbain") },
  { value: "Résidentiel", label: t("options.environnement.residentiel") },
  { value: "Rural", label: t("options.environnement.rural") },
  { value: "Commercial", label: t("options.environnement.commercial") },
  { value: "Mixte", label: t("options.environnement.mixte") },
  { value: "Touristique", label: t("options.environnement.touristique") },
  { value: "Industriel", label: t("options.environnement.industriel") },
  { value: "Verdoyant", label: t("options.environnement.verdoyant") },
];

export const getTypesSol = (t: TFunction) => [
  { value: "", label: "—" },
  { value: "Parquet", label: t("options.sol.parquet") },
  { value: "Stratifié", label: t("options.sol.stratifie") },
  { value: "Carrelage", label: t("options.sol.carrelage") },
  { value: "Pierre naturelle", label: t("options.sol.pierreNaturelle") },
  { value: "Marbre", label: t("options.sol.marbre") },
  { value: "Béton ciré", label: t("options.sol.betonCire") },
  { value: "Vinyl", label: t("options.sol.vinyl") },
  { value: "Moquette", label: t("options.sol.moquette") },
];

// Cave / Parking / Garage partagent les mêmes 4 choix : Non / Oui / Possible / Inclus.
const disponibiliteChoices = (t: TFunction) => [
  { value: "", label: "—" },
  { value: "Non", label: t("common.no") },
  { value: "Oui", label: t("common.yes") },
  { value: "Possible", label: t("options.possible") },
  { value: "Inclus", label: t("options.inclus") },
];

export const getCaveOptions = (t: TFunction) => disponibiliteChoices(t);
export const getParkingOptions = (t: TFunction) => disponibiliteChoices(t);
export const getGarageOptions = (t: TFunction) => disponibiliteChoices(t);

export const getChargesInclusesOptions = (t: TFunction) => [
  { value: "Charges communes", label: t("options.charges.communes") },
  { value: "Eau froide", label: t("options.charges.eauFroide") },
  { value: "Eau chaude", label: t("options.charges.eauChaude") },
  { value: "Chauffage", label: t("options.charges.chauffage") },
  { value: "Internet", label: t("options.charges.internet") },
  { value: "TV", label: t("options.charges.tv") },
  { value: "Assurance", label: t("options.charges.assurance") },
  { value: "Electricité", label: t("options.charges.electricite") },
];

export const getDisponibiliteOptions = (t: TFunction) => [
  // "en_preparation" masque du deroulant (user 2026-05-20) — les rows
  // existantes en DB avec ce statut sont gerees par la migration manuelle
  // qui les bascule en DISPONIBLE.
  { value: "disponible", label: t("options.disponibilite.disponible") },
  { value: "loue", label: t("options.disponibilite.loue") },
  { value: "vendu", label: t("options.disponibilite.vendu") },
];

export const getEtatsBatiment = (t: TFunction) => [
  { value: "", label: "—" },
  { value: "Neuf", label: t("options.etatBatiment.neuf") },
  { value: "Excellent état", label: t("options.etatBatiment.excellent") },
  { value: "Bon état", label: t("options.etatBatiment.bon") },
  { value: "À rafraîchir", label: t("options.etatBatiment.aRafraichir") },
  { value: "À rénover", label: t("options.etatBatiment.aRenover") },
];

// Mêmes clés/valeurs que le déroulant vente (venteFormFields.ts) — colonne
// `chauffageEnergie` partagée entre les deux fiches.
export const getChauffageEnergie = (t: TFunction) => [
  { value: "", label: "—" },
  { value: "gaz", label: t("vente.options.chauffageEnergie.gaz") },
  { value: "electrique", label: t("vente.options.chauffageEnergie.electrique") },
  { value: "accumulateurs", label: t("vente.options.chauffageEnergie.accumulateurs") },
  { value: "mazout", label: t("vente.options.chauffageEnergie.mazout") },
  { value: "chauffage_sol", label: t("vente.options.chauffageEnergie.chauffage_sol") },
  { value: "pompe_chaleur", label: t("vente.options.chauffageEnergie.pompe_chaleur") },
  { value: "bois_pellets", label: t("vente.options.chauffageEnergie.bois_pellets") },
];

// Aligné sur les valeurs Rentio : A+ à G (+ "-" pour NA/unknown/null).
// A++ supprimé car non présent dans les exports Rentio.
export const CLASSES_PEB = [
  { value: "", label: "—" },
  { value: "A+", label: "A+" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "D", label: "D" },
  { value: "E", label: "E" },
  { value: "F", label: "F" },
  { value: "G", label: "G" },
];

/**
 * Orientations d'une chambre / terrasse. Extrait de `venteFormFields.OPTS`
 * (chantier fiche en onglets 2026-08-08) : le répéteur surface + orientation
 * est désormais partagé par la fiche vente ET la fiche location, les deux
 * doivent proposer exactement les mêmes valeurs.
 */
export const getOrientations = (t: TFunction) => [
  { value: "", label: "—" },
  { value: "nord", label: t("vente.options.orientation.nord") },
  { value: "sud", label: t("vente.options.orientation.sud") },
  { value: "est", label: t("vente.options.orientation.est") },
  { value: "ouest", label: t("vente.options.orientation.ouest") },
  { value: "nord_est", label: t("vente.options.orientation.nord_est") },
  { value: "nord_ouest", label: t("vente.options.orientation.nord_ouest") },
  { value: "sud_est", label: t("vente.options.orientation.sud_est") },
  { value: "sud_ouest", label: t("vente.options.orientation.sud_ouest") },
];

/**
 * Années du bâti (construction / rénovation), de la plus récente à la plus
 * ancienne — on saisit bien plus souvent un immeuble récent qu'un hôtel de
 * maître de 1900.
 *
 * Plancher à 1900 : le parc géré est du logement urbain belge. À descendre si
 * un bien classé plus ancien entre en gestion.
 *
 * Borne haute = année courante + 2, pour les livraisons à venir (un bien en
 * construction se commercialise avant sa réception).
 *
 * ⚠️ `today()` est appelé DANS la fonction, jamais figé à l'import : une
 * constante de module se gèlerait au démarrage de l'app et deviendrait fausse
 * sur une session ouverte au passage d'une année (cf. lib/today.ts).
 */
export const getAnneesBati = (t: TFunction) => {
  const max = today().getFullYear() + 2;
  const annees = Array.from({ length: max - 1900 + 1 }, (_, i) => String(max - i));
  return [{ value: "", label: t("common.none", "—") }, ...annees.map((y) => ({ value: y, label: y }))];
};
